import axios, { AxiosError } from 'axios';
import { BASE_URL, SEASON_API_PATH } from 'constants/api';
import { ApiService } from 'service/api/apiService';
import { seasonApiService } from 'service/api/seasonApiService';
import { FetchResult } from 'type/apiType';
import {
  ElevenDbSeasonInterface,
  PosterInterface,
  SeasonApiRequest,
  SeasonData,
  SeasonInterface,
  SeasonListItem,
} from 'type/seasonType';
import { I18nApiErrorHandler } from '../../../shared/services/i18n/i18nApiErrorHandler';

class SeasonService {
  constructor(private apiService: ApiService<SeasonInterface>) {}

  public async fetch(query?: URLSearchParams): Promise<FetchResult<SeasonInterface>> {
    return this.apiService.fetch(query);
  }

  public async fetchById(
    seasonId: number,
    query?: Record<string, string>
  ): Promise<SeasonInterface> {
    if (!seasonId) {
      return {} as SeasonInterface;
    }
    const response = await this.apiService.fetchById(seasonId, query);
    return response;
  }

  public async fetchByIds(
    seasonIds: number[],
    query?: Record<string, string>
  ): Promise<FetchResult<SeasonInterface>> {
    if (seasonIds.length === 0) {
      return {
        results: [],
        count: 0,
        next: null,
        previous: null,
        page_next: null,
        page_previous: null,
        page_size: 0,
      };
    }
    return await this.apiService.fetchByIds(seasonIds, query);
  }

  public async create(season: SeasonInterface): Promise<SeasonInterface> {
    return await this.apiService.create(season);
  }

  public async update(seasonId: number, season: Partial<SeasonInterface>): Promise<void> {
    await this.apiService.update(seasonId, season);
  }

  public async delete(seasonId: number): Promise<void> {
    await this.apiService.delete(seasonId);
  }

  /**
   * Fetch Season list with i18n fields
   */
  public async fetchSeasonsWithI18n(
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonData>> {
    return seasonApiService.fetchSeasonListWithI18n(queryParams);
  }

  /**
   * Fetch single Season with i18n fields
   */
  public async fetchSeasonWithI18n(id: number): Promise<SeasonData> {
    return seasonApiService.fetchSeasonWithI18n(id);
  }

  /**
   * Create Season with both legacy and i18n fields
   */
  public async createSeasonWithI18n(seasonData: SeasonApiRequest): Promise<SeasonData> {
    return seasonApiService.createSeasonWithI18n(seasonData);
  }

  /**
   * Update Season with both legacy and i18n fields
   */
  public async updateSeasonWithI18n(
    id: number,
    seasonData: Partial<SeasonApiRequest>
  ): Promise<SeasonData> {
    return seasonApiService.updateSeasonWithI18n(id, seasonData);
  }

  /**
   * Fetch Season list for a specific language
   */
  public async fetchSeasonsByLanguage(
    language: string,
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonData>> {
    return seasonApiService.fetchSeasonsByLanguage(language, queryParams);
  }

  /**
   * Fetch Season by translation completeness
   */
  public async fetchSeasonsByTranslationStatus(
    requiredLanguages: string[] = ['ko'],
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonData>> {
    return seasonApiService.fetchSeasonsByTranslationStatus(requiredLanguages, queryParams);
  }

  /**
   * Fetch Season list for a specific Series (i18n support)
   */
  public async fetchSeasonsBySeriesWithI18n(
    seriesId: number,
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonData>> {
    return seasonApiService.fetchSeasonsBySeriesWithI18n(seriesId, queryParams);
  }

  /**
   * Fetch Season list with minimal fields for list page
   * Uses SeasonListSerializer
   */
  public async fetchSeasonList(
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonListItem>> {
    try {
      const params = new URLSearchParams(queryParams);

      // Uses SeasonListSerializer (automatically applied by backend)
      params.set('serializer', 'list');

      const response = await axios.get(`${BASE_URL}${SEASON_API_PATH}`, { params });
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  public async bulkDistribution(ids: number[], contentConsumerName: string): Promise<void> {
    await axios.post(`${BASE_URL}${SEASON_API_PATH}bulk-distribution/`, {
      ids,
      content_consumer_name: contentConsumerName,
    });
  }

  public async fetchPoster(seasonId: number): Promise<PosterInterface[]> {
    const response = await axios.get(`${BASE_URL}${SEASON_API_PATH}${seasonId}/poster/`);
    return response.data;
  }

  public async uploadPoster(seasonId: number, posters: PosterInterface[]): Promise<void> {
    await Promise.all(
      posters.map(async (poster: PosterInterface) => {
        const formData = new FormData();
        formData.append('file', poster.originFileObj!);
        formData.append('is_representative', poster.is_representative.toString());
        if (poster.language) {
          formData.append('language', poster.language);
        }
        if (poster.orientation) {
          formData.append('orientation', poster.orientation);
        }
        const response = await axios.post(
          `${BASE_URL}${SEASON_API_PATH}${seasonId}/poster/`,
          formData
        );
        return response.data;
      })
    );
  }

  public async removePoster(seasonId: number, posters: PosterInterface[]): Promise<void> {
    await Promise.all(
      posters.map(async (poster: PosterInterface) => {
        await axios.delete(`${BASE_URL}${SEASON_API_PATH}${seasonId}/poster/${poster.id}/`);
      })
    );
  }

  public async updatePoster(seasonId: number, poster: PosterInterface): Promise<void> {
    await axios.patch(`${BASE_URL}${SEASON_API_PATH}${seasonId}/poster/${poster.id}/`, poster);
  }

  public async fetchElevenDbSeason(seasonId: number): Promise<ElevenDbSeasonInterface> {
    return (await this.apiService.fetchChildItemsByParentId<ElevenDbSeasonInterface>(
      seasonId,
      'eleven-db/'
    )) as ElevenDbSeasonInterface;
  }

  public async updateElevenDbSeason(
    seasonId: number,
    elevenDbSeason: Partial<ElevenDbSeasonInterface>
  ): Promise<void> {
    await this.apiService.updateItemsForParent(seasonId, 'eleven-db/', elevenDbSeason);
  }

  public async createElevenDbSeason(
    seasonId: number,
    elevenDbSeason: ElevenDbSeasonInterface
  ): Promise<void> {
    await this.apiService.createItemsForParent(seasonId, 'eleven-db/', elevenDbSeason);
  }
}

export const seasonService = new SeasonService(new ApiService<SeasonInterface>(SEASON_API_PATH));
